import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addOfficialLetterhead, addReportFooter } from "../utils/pdfUtils";
import { formatDate } from "../utils/dateFormat";

/**
 * Generate Income Collected PDF report.
 * @param {object} centralisedReport - The report data from the API
 * @param {number} reportMonth - 1-12
 * @param {number} reportYear - e.g. 2026
 */
export function generateCentralisedPDF(centralisedReport, reportMonth, reportYear) {
  if (!centralisedReport) return;

  const doc = new jsPDF();
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const contentStartY = addOfficialLetterhead(
    doc,
    "INCOME COLLECTED REPORT",
    `${monthNames[reportMonth - 1]} ${reportYear}`,
    { r: 0, g: 51, b: 102 }
  );

  // Summary boxes
  doc.setFillColor(240, 245, 250);
  doc.rect(15, contentStartY, 55, 22, "F");
  doc.rect(77, contentStartY, 55, 22, "F");
  doc.rect(139, contentStartY, 55, 22, "F");

  doc.setDrawColor(200, 200, 200);
  doc.rect(15, contentStartY, 55, 22);
  doc.rect(77, contentStartY, 55, 22);
  doc.rect(139, contentStartY, 55, 22);

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text("Karaoke Revenue", 42, contentStartY + 7, { align: "center" });
  doc.text("BBQ Revenue", 104, contentStartY + 7, { align: "center" });
  doc.text("Grand Total", 166, contentStartY + 7, { align: "center" });

  doc.setFontSize(12);
  doc.setTextColor(0, 51, 102);
  doc.setFont("helvetica", "bold");
  const karaokeTotal = Number(centralisedReport.karaoke_total) || 0;
  const bbqTotal = Number(centralisedReport.bbq_total) || 0;
  const grandTotal = Number(centralisedReport.grand_total) || 0;
  doc.text(`$${karaokeTotal.toFixed(2)}`, 42, contentStartY + 17, { align: "center" });
  doc.text(`$${bbqTotal.toFixed(2)}`, 104, contentStartY + 17, { align: "center" });
  doc.text(`$${grandTotal.toFixed(2)}`, 166, contentStartY + 17, { align: "center" });

  // Table
  const bookings = centralisedReport.bookings || [];
  const tableData = bookings.map((b) => {
    let txnDate = "-";
    if (b.transaction_date) {
      try {
        txnDate = new Date(b.transaction_date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        txnDate = "-";
      }
    }
    return [
      formatDate(b.date) || b.date || "",
      txnDate,
      b.facility || "",
      b.booking_code || "",
      b.resident_name || b.name || "",
      `$${Number(b.amount || b.price || 0).toFixed(2)}`,
      b.transaction_number || "-",
    ];
  });

  if (tableData.length > 0) {
    autoTable(doc, {
      startY: contentStartY + 30,
      head: [["Booking Date", "Transaction Date", "Facility", "Code", "Name", "Amount", "onePA Txn#"]],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text("No bookings found for this period", 105, contentStartY + 43, { align: "center" });
  }

  addReportFooter(doc, { r: 0, g: 51, b: 102 });
  doc.save(`Income_Report_${reportYear}_${String(reportMonth).padStart(2, "0")}.pdf`);
  return true;
}

/**
 * Generate KJ Allowance Report PDF.
 * @param {object} kjReport - The KJ report data from the API
 * @param {number} kjReportMonth - 1-12
 * @param {number} kjReportYear - e.g. 2026
 * @param {string} kjFilterName - Optional KJ name filter (empty = All)
 */
export function generateKjAllowancePDF(kjReport, kjReportMonth, kjReportYear, kjFilterName = '', filteredEntries = null, payFilterLabel = 'All') {
  if (!kjReport) return;

  const entries = filteredEntries || kjReport.entries || [];
  const isFiltered = filteredEntries && (payFilterLabel !== 'All' || kjFilterName);

  const doc = new jsPDF();
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const subtitle = isFiltered
    ? `${monthNames[kjReportMonth - 1]} ${kjReportYear} — ${payFilterLabel}${kjFilterName ? ` | ${kjFilterName}` : ''}`
    : `${monthNames[kjReportMonth - 1]} ${kjReportYear}`;

  const contentStartY = addOfficialLetterhead(
    doc,
    "KJ ALLOWANCE REPORT",
    subtitle,
    { r: 128, g: 0, b: 128 }
  );

  // Summary boxes
  const filteredTotal = entries.reduce((s, e) => s + e.payout, 0);
  const filteredPending = entries.filter(e => !e.kj_paid && e.payout > 0);
  const filteredPaid = entries.filter(e => e.kj_paid && e.payout > 0);

  doc.setFillColor(250, 245, 255);
  doc.rect(15, contentStartY, 42, 22, "F");
  doc.rect(62, contentStartY, 42, 22, "F");
  doc.rect(109, contentStartY, 42, 22, "F");
  doc.rect(156, contentStartY, 42, 22, "F");

  doc.setDrawColor(200, 200, 200);
  doc.rect(15, contentStartY, 42, 22);
  doc.rect(62, contentStartY, 42, 22);
  doc.rect(109, contentStartY, 42, 22);
  doc.rect(156, contentStartY, 42, 22);

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text("Sessions", 36, contentStartY + 7, { align: "center" });
  doc.text("Paid Out", 83, contentStartY + 7, { align: "center" });
  doc.text("Pending", 130, contentStartY + 7, { align: "center" });
  doc.text("Total Payout", 177, contentStartY + 7, { align: "center" });

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(128, 0, 128);
  doc.text(`${entries.length}`, 36, contentStartY + 17, { align: "center" });
  doc.setTextColor(34, 139, 34);
  doc.text(`$${filteredPaid.reduce((s, e) => s + e.payout, 0)}`, 83, contentStartY + 17, { align: "center" });
  doc.setTextColor(220, 38, 38);
  doc.text(`$${filteredPending.reduce((s, e) => s + e.payout, 0)}`, 130, contentStartY + 17, { align: "center" });
  doc.setTextColor(218, 165, 32);
  doc.text(`$${filteredTotal}`, 177, contentStartY + 17, { align: "center" });

  // Table with Payment column
  const tableData = entries.map((e) => [
    e.kj_name,
    formatDate(e.date),
    e.slot_time || (e.slot_type === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM'),
    e.status_tag || e.kj_type || '-',
    `$${e.payout}`,
    e.payout === 0 ? 'N/A' : e.kj_paid ? 'PAID' : 'PENDING',
  ]);

  if (tableData.length > 0) {
    autoTable(doc, {
      startY: contentStartY + 30,
      head: [["KJ Name", "Date", "Time", "KJ Type", "Payout", "Payment"]],
      body: tableData,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [128, 0, 128], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [250, 245, 255] },
      columnStyles: {
        4: { halign: 'right', fontStyle: 'bold' },
        5: { halign: 'center', fontStyle: 'bold' },
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 5) {
          if (hookData.cell.raw === 'PENDING') {
            hookData.cell.styles.textColor = [220, 38, 38];
          } else if (hookData.cell.raw === 'PAID') {
            hookData.cell.styles.textColor = [22, 163, 74];
          }
        }
      },
      foot: [
        [
          {
            content: `Total Payout: $${filteredTotal}`,
            colSpan: 5,
            styles: { halign: "right", fontStyle: "bold" },
          },
          {
            content: `${filteredPending.length} pending`,
            styles: { halign: "center", fontStyle: "bold", textColor: [220, 38, 38] },
          },
        ],
      ],
      footStyles: { fillColor: [255, 251, 235] },
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text("No KJ sessions for this period", 105, contentStartY + 43, { align: "center" });
  }

  addReportFooter(doc, { r: 128, g: 0, b: 128 });
  const kjSuffix = kjFilterName ? kjFilterName.replace(/\s+/g, '_') : 'All';
  doc.save(`KJ_Allowance_Report_${kjReportYear}_${String(kjReportMonth).padStart(2, "0")}_${kjSuffix}.pdf`);
  return true;
}


/**
 * Generate Monthly P/L Report PDF.
 * @param {object} plData - Monthly P/L data from /api/admin/finance/profit-loss
 * @param {object|null} plAnnual - Annual P/L data from /api/admin/finance/profit-loss-annual (optional)
 */
export function generateProfitLossPDF(plData, plAnnual = null) {
  if (!plData) return;

  const doc = new jsPDF();
  const accentColor = { r: 30, g: 41, b: 59 }; // slate-800

  const contentStartY = addOfficialLetterhead(
    doc,
    "MONTHLY PROFIT & LOSS REPORT",
    `${plData.month_name} ${plData.year}`,
    accentColor
  );

  let y = contentStartY;

  // ── Summary Boxes ──
  const boxW = 42;
  const boxH = 22;
  const boxes = [
    { label: "Total Revenue", value: `$${plData.revenue.total_revenue}`, bg: [220, 252, 231] },
    { label: "KJ Costs", value: `$${plData.costs.total_kj_cost}`, bg: [254, 226, 226] },
    { label: "Net Profit", value: `$${plData.profit.net_profit}`, bg: [219, 234, 254] },
    { label: "Subsidy Given", value: `$${plData.subsidy.total_discounts}`, bg: [254, 243, 199] },
  ];

  boxes.forEach((box, i) => {
    const x = 15 + i * (boxW + 3);
    doc.setFillColor(...box.bg);
    doc.rect(x, y, boxW, boxH, "F");
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, boxW, boxH);
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    doc.setFont("helvetica", "normal");
    doc.text(box.label, x + boxW / 2, y + 7, { align: "center" });
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    doc.setFont("helvetica", "bold");
    doc.text(box.value, x + boxW / 2, y + 17, { align: "center" });
  });

  y += boxH + 10;

  // ── Facility Breakdown Table ──
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text("Facility Breakdown", 15, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    head: [["", "Karaoke", "BBQ Pit", "Total"]],
    body: [
      [
        "Gross Revenue",
        `$${plData.revenue.karaoke_gross}`,
        `$${plData.revenue.bbq_net}`,
        `$${plData.revenue.karaoke_gross + plData.revenue.bbq_net}`,
      ],
      [
        "Less: Discounts",
        `-$${plData.revenue.karaoke_discounts}`,
        "$0",
        `-$${plData.revenue.karaoke_discounts}`,
      ],
      [
        "Net Revenue",
        `$${plData.revenue.karaoke_net}`,
        `$${plData.revenue.bbq_net}`,
        `$${plData.revenue.total_revenue}`,
      ],
      [
        "Less: KJ Costs",
        `-$${plData.costs.total_kj_cost}`,
        "$0",
        `-$${plData.costs.total_kj_cost}`,
      ],
      [
        "NET PROFIT",
        `$${plData.profit.karaoke_profit}`,
        `$${plData.profit.bbq_profit}`,
        `$${plData.profit.net_profit}`,
      ],
    ],
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 40 },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right", fontStyle: "bold" },
    },
    bodyStyles: { lineWidth: 0.1, lineColor: [200, 200, 200] },
    footStyles: { fillColor: [241, 245, 249] },
    didParseCell: function (data) {
      if (data.row.index === 4) {
        data.cell.styles.fillColor = [241, 245, 249];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ── Booking Mix ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Booking Mix", 15, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    head: [["Type", "Count", "Revenue", "Share"]],
    body: [
      [
        "Standard Bookings",
        `${plData.bookings.standard_count}`,
        `$${plData.bookings.standard_revenue}`,
        `${plData.bookings.karaoke_count > 0 ? Math.round((plData.bookings.standard_count / plData.bookings.karaoke_count) * 100) : 0}%`,
      ],
      [
        "Special / Discounted",
        `${plData.bookings.special_count}`,
        `$${plData.bookings.special_revenue}`,
        `${plData.bookings.karaoke_count > 0 ? Math.round((plData.bookings.special_count / plData.bookings.karaoke_count) * 100) : 0}%`,
      ],
      [
        "BBQ Pit Bookings",
        `${plData.bookings.bbq_count}`,
        `$${plData.revenue.bbq_net}`,
        "-",
      ],
    ],
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "right" },
      3: { halign: "center" },
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ── KJ Margin Indicator ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("KJ Cost Analysis", 15, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(`KJ sessions this month: ${plData.costs.kj_sessions}`, 15, y);
  y += 5;
  doc.text(`Total KJ payout: $${plData.costs.total_kj_cost}`, 15, y);
  y += 5;
  doc.text(`KJ cost as % of Karaoke revenue: ${plData.profit.kj_margin_pct}%`, 15, y);
  y += 4;

  // Draw margin bar
  const barX = 15;
  const barW = 120;
  const barH = 6;
  doc.setFillColor(229, 231, 235);
  doc.rect(barX, y, barW, barH, "F");
  const pct = Math.min(plData.profit.kj_margin_pct, 100);
  if (pct > 50) doc.setFillColor(239, 68, 68);
  else if (pct > 30) doc.setFillColor(245, 158, 11);
  else doc.setFillColor(34, 197, 94);
  doc.rect(barX, y, (barW * pct) / 100, barH, "F");
  doc.setDrawColor(150, 150, 150);
  doc.rect(barX, y, barW, barH);
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.text(`${plData.profit.kj_margin_pct}%`, barX + barW + 5, y + 5);

  // ── Annual Table (page 2 if data exists) ──
  if (plAnnual) {
    doc.addPage();
    const annualY = addOfficialLetterhead(
      doc,
      "ANNUAL P/L SUMMARY",
      `Year ${plAnnual.year}`,
      accentColor
    );

    autoTable(doc, {
      startY: annualY,
      head: [["Month", "Karaoke", "BBQ", "Revenue", "KJ Cost", "Discounts", "Net Profit"]],
      body: plAnnual.monthly.map((r) => [
        r.month_name,
        `$${r.karaoke_revenue}`,
        `$${r.bbq_revenue}`,
        `$${r.total_revenue}`,
        r.kj_cost > 0 ? `-$${r.kj_cost}` : "-",
        r.discounts > 0 ? `$${r.discounts}` : "-",
        `$${r.net_profit}`,
      ]),
      foot: [[
        "TOTAL",
        `$${plAnnual.totals.total_revenue - plAnnual.monthly.reduce((s, r) => s + r.bbq_revenue, 0)}`,
        `$${plAnnual.monthly.reduce((s, r) => s + r.bbq_revenue, 0)}`,
        `$${plAnnual.totals.total_revenue}`,
        `-$${plAnnual.totals.total_kj_cost}`,
        `$${plAnnual.totals.total_discounts}`,
        `$${plAnnual.totals.total_profit}`,
      ]],
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
      footStyles: { fillColor: [241, 245, 249], fontStyle: "bold" },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right", fontStyle: "bold" },
        4: { halign: "right", textColor: [220, 38, 38] },
        5: { halign: "right", textColor: [217, 119, 6] },
        6: { halign: "right" },
      },
      didParseCell: function (data) {
        if (data.section === "body") {
          const val = plAnnual.monthly[data.row.index]?.net_profit || 0;
          if (data.column.index === 6) {
            data.cell.styles.textColor = val >= 0 ? [21, 128, 61] : [220, 38, 38];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
    });

    addReportFooter(doc, accentColor);
  }

  // Footer on page 1
  doc.setPage(1);
  addReportFooter(doc, accentColor);

  doc.save(`PL_Report_${plData.year}_${String(plData.month).padStart(2, "0")}.pdf`);
  return true;
}


/**
 * Generate Outstanding Payments Report PDF.
 * @param {object} data - Outstanding payments data from the API
 */
export function generateOutstandingPaymentsPDF(data) {
  if (!data) return;

  const doc = new jsPDF();
  const accentColor = { r: 185, g: 28, b: 28 }; // red-700

  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const contentStartY = addOfficialLetterhead(
    doc,
    "OUTSTANDING PAYMENTS REPORT",
    `As at ${today}`,
    accentColor
  );

  let y = contentStartY;

  // Summary boxes
  const boxes = [
    { label: "Karaoke Pending", value: `${data.summary.karaoke_pending}`, bg: [254, 243, 199] },
    { label: "Completed Unpaid", value: `${data.summary.karaoke_completed_unpaid}`, bg: [254, 226, 226] },
    { label: "BBQ Pending", value: `${data.summary.bbq_pending}`, bg: [255, 237, 213] },
    { label: "Total Outstanding", value: `$${data.summary.total_outstanding}`, bg: [254, 226, 226] },
  ];

  const boxW = 42;
  const boxH = 22;
  boxes.forEach((box, i) => {
    const x = 15 + i * (boxW + 3);
    doc.setFillColor(...box.bg);
    doc.rect(x, y, boxW, boxH, "F");
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, boxW, boxH);
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    doc.setFont("helvetica", "normal");
    doc.text(box.label, x + boxW / 2, y + 7, { align: "center" });
    doc.setFontSize(13);
    doc.setTextColor(185, 28, 28);
    doc.setFont("helvetica", "bold");
    doc.text(box.value, x + boxW / 2, y + 17, { align: "center" });
  });

  y += boxH + 10;

  // Table
  const entries = data.entries || [];
  if (entries.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [["Facility", "Category", "Code", "Name", "Date", "Slot", "Amount"]],
      body: entries.map((e) => [
        e.facility,
        e.category,
        e.booking_code,
        e.name,
        e.date,
        e.slot,
        `$${e.amount}`,
      ]),
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [185, 28, 28], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [255, 248, 248] },
      columnStyles: {
        6: { halign: "right", fontStyle: "bold" },
      },
      foot: [[
        { content: `Total Outstanding: $${data.summary.total_outstanding}`, colSpan: 7, styles: { halign: "right", fontStyle: "bold", fillColor: [254, 226, 226] } },
      ]],
    });
  } else {
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text("No outstanding payments found.", 105, y + 15, { align: "center" });
  }

  addReportFooter(doc, accentColor);
  doc.save(`Outstanding_Payments_${today.replace(/\s+/g, '_')}.pdf`);
  return true;
}
